import { TypePolicy } from '@apollo/client';
import * as types from 'generated/graphql';
import * as address from './address';
import * as apiFile from './apiFile';
import * as area from './area';
import * as auditLogEvent from './auditLogEvent';
import * as clientAuthorizationStatus from './clientAuthorizationStatus';
import * as clientReport from './clientReport';
import * as clientReportLink from './clientReportLink';
import * as comment from './comment';
import * as dailyReport from './dailyReport';
import * as decision from './decision';
import * as decisionType from './decisionType';
import * as document from './document';
import * as fileReservation from './fileReservation';
import * as folder from './folder';
import * as incident from './incident';
import * as inspection from './inspection';
import * as inspectionResult from './inspectionResult';
import * as installation from './installation';
import * as installationStatus from './installationStatus';
import * as installationType from './installationType';
import * as invite from './invite';
import * as materialDelivery from './materialDelivery';
import * as membership from './membership';
import * as metaDelivery from './metaDelivery';
import * as metaField from './metaField';
import * as metaFieldOptions from './metaFieldOptions';
import * as metaInspection from './metaInspection';
import * as metaMilestone from './metaMilestone';
import * as metaPhoto from './metaPhoto';
import * as milestone from './milestone';
import * as milestoneStatus from './milestoneStatus';
import * as mutation from './mutation';
import * as notification from './notification';
import * as onSiteWorkEvent from './onSiteWorkEvent';
import * as pageInfo from './pageInfo';
import * as person from './person';
import * as plan from './plan';
import * as planSet from './planSet';
import * as planVersion from './planVersion';
import * as product from './product';
import * as project from './project';
import * as query from './query';
import * as reportAttachment from './reportAttachment';
import * as role from './role';
import * as room from './room';
import * as siteUserFull from './siteUserFull';
import * as standardAreaInstallation from './standardAreaInstallation';
import * as standardProjectTemplate from './standardProjectTemplate';
import * as standardTemplateMilestone from './standardTemplateMilestone';
import * as subArea from './subArea';
import * as task from './task';
import * as taskCategory from './taskCategory';
import * as taskStatus from './taskStatus';
import {
  IRoleAccess,
  IRoleActionPolicy
} from './types';
import * as usState from './usState';



type TypePolicies = {
  [__typename: string]: TypePolicy;
};

const typePolicies: (keyof TypePolicies)[] = [
  'Address',
  'ApiFile',
  'Area',
  'AuditLogEvent',
  'ClientAuthorizationStatus',
  'ClientReport',
  'ClientReportLink',
  'Comment',
  'DailyReport',
  'Decision',
  'DecisionType',
  'Document',
  'FileReservation',
  'Folder',
  'Incident',
  'Inspection',
  'InspectionResult',
  'Installation',
  'InstallationStatus',
  'InstallationType',
  'Invite',
  'MaterialDelivery',
  'Membership',
  'MetaDelivery',
  'MetaField',
  'MetaFieldOptions',
  'MetaInspection',
  'MetaMilestone',
  'MetaPhoto',
  'Milestone',
  'MilestoneStatus',
  'Mutation',
  'Notification',
  'OnSiteWorkEvent',
  'PageInfo',
  'Person',
  'Plan',
  'PlanSet',
  'PlanVersion',
  'Product',
  'Project',
  'Query',
  'ReportAttachment',
  'Role',
  'Room',
  'SiteUserFull',
  'StandardAreaInstallation',
  'StandardProjectTemplate',
  'StandardTemplateMilestone',
  'SubArea',
  'Task',
  'TaskCategory',
  'TaskStatus',
  'UsState'
] as const;

const accessPolicies: Record<typeof typePolicies[number], IRoleAccess> = {
  Address: address.policy,
  ApiFile: apiFile.policy,
  Area: area.policy,
  AuditLogEvent: auditLogEvent.policy,
  ClientAuthorizationStatus: clientAuthorizationStatus.policy,
  ClientReport: clientReport.policy,
  ClientReportLink: clientReportLink.policy,
  Comment: comment.policy,
  DailyReport: dailyReport.policy,
  Decision: decision.policy,
  DecisionType: decisionType.policy,
  Document: document.policy,
  FileReservation: fileReservation.policy,
  Folder: folder.policy,
  Incident: incident.policy,
  Inspection: inspection.policy,
  InspectionResult: inspectionResult.policy,
  Installation: installation.policy,
  InstallationStatus: installationStatus.policy,
  InstallationType: installationType.policy,
  Invite: invite.policy,
  MaterialDelivery: materialDelivery.policy,
  Membership: membership.policy,
  MetaDelivery: metaDelivery.policy,
  MetaField: metaField.policy,
  MetaFieldOptions: metaFieldOptions.policy,
  MetaInspection: metaInspection.policy,
  MetaMilestone: metaMilestone.policy,
  MetaPhoto: metaPhoto.policy,
  Milestone: milestone.policy,
  MilestoneStatus: milestoneStatus.policy,
  Mutation: mutation.policy,
  Notification: notification.policy,
  OnSiteWorkEvent: onSiteWorkEvent.policy,
  PageInfo: pageInfo.policy,
  Person: person.policy,
  Plan: plan.policy,
  PlanSet: planSet.policy,
  PlanVersion: planVersion.policy,
  Product: product.policy,
  Project: project.policy,
  Query: query.policy,
  ReportAttachment: reportAttachment.policy,
  Role: role.policy,
  Room: room.policy,
  SiteUserFull: siteUserFull.policy,
  StandardAreaInstallation: standardAreaInstallation.policy,
  StandardProjectTemplate: standardProjectTemplate.policy,
  StandardTemplateMilestone: standardTemplateMilestone.policy,
  SubArea: subArea.policy,
  Task: task.policy,
  TaskCategory: taskCategory.policy,
  TaskStatus: taskStatus.policy,
  UsState: usState.policy
};

export function getEntityAccess(roles: types.RoleName[], entityName: keyof TypePolicies, isSuperAdmin?: boolean) {
  if (isSuperAdmin) {
    return {
      create: true,
      delete: true,
      update: true,
      view: true
    };
  }

  const defaultCase: IRoleActionPolicy = {
    create: false,
    delete: false,
    update: false,
    view: false
  };



  const controlPolicy = accessPolicies[entityName];
  if (!controlPolicy) {
    return defaultCase;
  }

  roles.forEach((role) => {
    const rolePolicy = controlPolicy[role];
    if (!defaultCase.create && rolePolicy?.create) {
      defaultCase.create = true;
    }
    if (!defaultCase.update && rolePolicy?.update) {
      defaultCase.update = true;
    }
    if (!defaultCase.view && rolePolicy?.view) {
      defaultCase.view = true;
    }
    if (!defaultCase.delete && rolePolicy?.delete) {
      defaultCase.delete = true;
    }
  });
  return defaultCase;
}

